import React, { useEffect, useState } from "react";
import { Alert, Container } from "../../../components";
import { Card, Icon as TeddyIcon, Text, Heading, Button } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import { connect, useDispatch } from "react-redux";
import { REFILL_LABELS } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { RefillPackage } from "../../../../../models/state/prepaid";
import { getErrorMessage } from "../../../app/state/selectors";
import { trackFunnelEvent, trackRefillProductClick } from "../../../google-analytics";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import BackLink from "../../components/atoms/BackLink/BackLink";
import PackageButton from "../../components/molecules/PackageButton/PackageButton";
import { setRefillOption } from "../../state/actions/refillActions";
import {
    getFallBackUrl,
    isRefillSelected,
    prepaidDisclaimers,
    refillPackages,
    selectedPhonenumber,
} from "../../state/selectors";
import TEXT from "../../text-consts";
import "./ChooseRefillPackage.less";
interface StateProps {
    disclaimers: {
        daily: string;
        data: string;
        international: string;
        monthly: string;
        topup: string;
    };
    fallBackUrl: string;
    isRefillOptionSelected: boolean;
    logicalErrorMessage?: string;
    packages: {
        daily: RefillPackage[];
        data: RefillPackage[];
        international: RefillPackage[];
        monthly: RefillPackage[];
        topup: RefillPackage[];
    };
    selectedNumber: string;
}

type PrepaidRefillProps = StateProps;

type PackageType = "monthly" | "data" | "daily" | "topup" | "international" | "";

type PrepaidPackagesType = "MONTHLY" | "DAILY" | "TOPUP" | "DATA" | "INTERNATIONAL";

const renderPackages = (packages: RefillPackage[], onClick: (code: RefillPackage) => void) => {
    if (_isEmpty(packages)) {
        return null;
    }

    return packages
        .map((item) => (
            <PackageButton
                key={item.code}
                size={item.size}
                price={item.price}
                onClick={() => onClick(item)}
                duration={item.duration}
            />
        ))
        .sort((a, b) => b.props.price - a.props.price);
};

const renderDisclaimers = (disclaimer: string) => {
    return <div className="prepaid-refill-package__disclaimers" dangerouslySetInnerHTML={{ __html: disclaimer }} />;
};

const PackageTypeButton = ({
    text,
    onClick,
    packages,
    ingress,
}: {
    ingress: string;
    onClick: () => void;
    packages: RefillPackage[];
    text: string;
}) => {
    return (
        !_isEmpty(packages) && (
            <>
                <Card
                    className="prepaid-refill-package__packageType"
                    layout="navigation-horizontal-large"
                    bordered
                    variant="white"
                >
                    <Card.Heading className="prepaid-refill-package__packageType--heading">{text}</Card.Heading>
                    <Card.Button variant="secondary" aria-label="Velg" iconOnly onClick={onClick}>
                        <TeddyIcon name="arrow-right" aria-hidden="true" focusable="false" />
                    </Card.Button>
                    <Card.Content>{ingress}</Card.Content>
                </Card>
            </>
        )
    );
};

const PrepaidRefill: React.FC<PrepaidRefillProps> = ({
    isRefillOptionSelected,
    packages,
    selectedNumber,
    disclaimers,
    fallBackUrl,
    logicalErrorMessage,
}) => {
    const [packageType, setSelectedPackageType] = useState<PackageType>("");
    const dispatch = useDispatch();

    useEffect(() => {
        if (isRefillOptionSelected) {
            window.location.assign(PAGES.PREPAID.REFILL.SUMMARY.URL);
        }
    }, [isRefillOptionSelected]);

    const handleAddRefillOption = (option: RefillPackage) => {
        dispatch(setRefillOption(option.code));

        trackFunnelEvent({
            step: "2",
            label: REFILL_LABELS[1],
            name: "Refill - Kontantkort påfyll",
            isUIEvent: true,
            uiEventName: "Velg påfyllingsprodukt",
        });
        trackRefillProductClick(option);
    };

    return (
        <div className="prepaid-refill-package">
            <div className="prepaid-refill-package__heading-container">
                <div className="prepaid-refill-package__heading-inner">
                    <div className="prepaid-refill-package__heading-wrapper">
                        <Heading mb="100" as="h1" variant="title-600" className="prepaid-refill-package__heading">
                            {(packageType && TEXT.REFILL.CHOOSE_PACKAGE_TYPE.HEADING) ||
                                TEXT.REFILL.CHOOSE_PACKAGE.HEADING}
                        </Heading>
                        {selectedNumber && (
                            <div className="prepaid-refill-package__selectNumberBox">
                                <div className="prepaid-refill-package__selectedNumber">
                                    <Text mb="100">
                                        Valgt mobilnummer: <span>{getFormattedPhoneNumber(selectedNumber)}</span>
                                    </Text>
                                </div>
                            </div>
                        )}
                        {logicalErrorMessage && (
                            <Container size="small">
                                <Alert kind="warning">{logicalErrorMessage}</Alert>
                            </Container>
                        )}
                        {packageType && (
                            <Heading mb="100" as="h2" variant="title-200">
                                {TEXT.REFILL.PACKAGETYPE[`${packageType.toUpperCase() as PrepaidPackagesType}`].HEADING}
                            </Heading>
                        )}
                        <Text mb="100">
                            {(packageType &&
                                TEXT.REFILL.PACKAGETYPE[`${packageType.toUpperCase() as PrepaidPackagesType}`]
                                    .PAGE_INGRESS) ||
                                TEXT.REFILL.CHOOSE_PACKAGE.INGRESS}
                        </Text>
                    </div>
                    <Container className="prepaid-refill-package__packages">
                        {_isEmpty(packageType) && (
                            <>
                                <>
                                    <Heading mb="100" as="h2" variant="title-200">
                                        {"Datapakker"}
                                    </Heading>
                                    <PackageTypeButton
                                        text={TEXT.REFILL.PACKAGETYPE.MONTHLY.HEADING}
                                        onClick={() => setSelectedPackageType("monthly")}
                                        packages={packages.monthly}
                                        ingress={TEXT.REFILL.PACKAGETYPE.MONTHLY.PRODUCT_INGRESS}
                                    />
                                    <PackageTypeButton
                                        text={TEXT.REFILL.PACKAGETYPE.DATA.HEADING}
                                        onClick={() => setSelectedPackageType("data")}
                                        packages={packages.data}
                                        ingress={TEXT.REFILL.PACKAGETYPE.DATA.PRODUCT_INGRESS}
                                    />
                                    <PackageTypeButton
                                        text={TEXT.REFILL.PACKAGETYPE.DAILY.HEADING}
                                        onClick={() => setSelectedPackageType("daily")}
                                        packages={packages.daily}
                                        ingress={TEXT.REFILL.PACKAGETYPE.DAILY.PRODUCT_INGRESS}
                                    />
                                </>
                                <>
                                    <Heading mb="100" as="h2" variant="title-200">
                                        {"Kronepåfyll"}
                                    </Heading>
                                    <PackageTypeButton
                                        text={TEXT.REFILL.PACKAGETYPE.TOPUP.HEADING}
                                        onClick={() => setSelectedPackageType("topup")}
                                        packages={packages.topup}
                                        ingress={TEXT.REFILL.PACKAGETYPE.TOPUP.PRODUCT_INGRESS}
                                    />
                                    <PackageTypeButton
                                        text={TEXT.REFILL.PACKAGETYPE.INTERNATIONAL.HEADING}
                                        onClick={() => setSelectedPackageType("international")}
                                        packages={packages.international}
                                        ingress={TEXT.REFILL.PACKAGETYPE.INTERNATIONAL.PRODUCT_INGRESS}
                                    />
                                </>
                            </>
                        )}
                        {packageType && renderPackages(packages[packageType], handleAddRefillOption)}
                        {disclaimers && packageType && renderDisclaimers(disclaimers[packageType])}
                        {!fallBackUrl && packageType && (
                            <Button
                                className="prepaid-refill-package__back-button"
                                variant="text"
                                type="button"
                                onClick={() => setSelectedPackageType("")}
                            >
                                <TeddyIcon name="arrow-left" /> Tilbake
                            </Button>
                        )}

                        {!fallBackUrl && !packageType && (
                            <div className="prepaid-refill-package__back-button">
                                <BackLink text="Tilbake" href={PAGES.PREPAID.REFILL.URL} />
                            </div>
                        )}
                        {fallBackUrl && !packageType && <BackLink text="Tilbake til MinSide" href={fallBackUrl} />}
                    </Container>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    packages: refillPackages(state),
    selectedNumber: selectedPhonenumber(state),
    disclaimers: prepaidDisclaimers(state),
    fallBackUrl: getFallBackUrl(state),
    isRefillOptionSelected: isRefillSelected(state),
    logicalErrorMessage: getErrorMessage(state),
});

export default connect(mapStateToProps)(PrepaidRefill);
